/**
 * Afuna Aromatics — blend-family integrated gallery.
 * Founder mobile-QA (2026-08-29) + Consolidated refinement (2026-08-29, WP B: circular navigation).
 *
 * One carousel per family page. Manual, NON-autoplaying.
 *   - No JS: the viewport is a native horizontal scroller with scroll-snap;
 *     every slide reachable by swipe/drag. Thumbnails are plain buttons.
 *   - With JS: prev/next wrap around the ends, thumbnail selection is direct,
 *     keyboard ArrowLeft/Right wrap, Home/End jump to the absolute ends, an
 *     aria-live readout reflects the true logical position after wrapping,
 *     and one extra boundary swipe/wheel gesture past the last (or first)
 *     slide wraps to the other end with an instant (non-animated) cut.
 *   - No cloned slides — assistive tech only ever sees the real N slides.
 *   - No infinite animation, no auto-advance timer, no external library,
 *     no page-level horizontal scroll. Reduced motion => instant moves.
 */
(function () {
  'use strict';

  var WRAP_COOLDOWN_MS = 500; // debounce one boundary gesture -> one wrap

  function initGallery(root) {
    var viewport = root.querySelector('[data-gallery-viewport]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var live = root.querySelector('[data-gallery-live]');
    if (!viewport || slides.length < 2) return;

    var total = slides.length;
    var index = 0;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var wrapLockUntil = 0;

    function wrapIndex(i) {
      return ((i % total) + total) % total;
    }

    function render() {
      thumbs.forEach(function (t, i) {
        var selected = i === index;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
      });
      // prev/next never disable — navigation is circular
      if (live) live.textContent = 'Image ' + (index + 1) + ' of ' + total;
    }

    function scrollToIndex(behavior) {
      var slide = slides[index];
      if (!slide) return;
      viewport.scrollTo({
        left: slide.offsetLeft - viewport.offsetLeft,
        behavior: behavior || (reduceMotion.matches ? 'auto' : 'smooth'),
      });
    }

    function goTo(i, opts) {
      opts = opts || {};
      var next = wrapIndex(i);
      if (next === index && !opts.force) return;
      // A wrap (last<->first) uses an instant cut so it never reads as a long
      // unstable scroll-back; ordinary adjacent moves stay smooth.
      var wrapped = Math.abs(next - index) === total - 1;
      index = next;
      render();
      if (opts.scroll !== false) {
        scrollToIndex(opts.behavior || (wrapped ? 'auto' : undefined));
      }
      if (opts.focusThumb && thumbs[index]) thumbs[index].focus();
    }

    function atMaxScroll() {
      return viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 2;
    }
    function atMinScroll() {
      return viewport.scrollLeft <= 2;
    }
    function now() {
      return Date.now ? Date.now() : new Date().getTime();
    }
    function tryBoundaryWrap(direction) {
      if (now() < wrapLockUntil) return false;
      if (direction > 0 && index === total - 1 && atMaxScroll()) {
        wrapLockUntil = now() + WRAP_COOLDOWN_MS;
        goTo(0, { behavior: 'auto' });
        return true;
      }
      if (direction < 0 && index === 0 && atMinScroll()) {
        wrapLockUntil = now() + WRAP_COOLDOWN_MS;
        goTo(total - 1, { behavior: 'auto' });
        return true;
      }
      return false;
    }

    // Prev / Next — circular
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });

    // Thumbnails (roving tabindex tablist) — direct and deterministic
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(i); });
      t.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            goTo(index + 1, { focusThumb: true });
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            goTo(index - 1, { focusThumb: true });
            break;
          case 'Home':
            e.preventDefault();
            goTo(0, { focusThumb: true });
            break;
          case 'End':
            e.preventDefault();
            goTo(total - 1, { focusThumb: true });
            break;
        }
      });
    });

    // Keyboard on the carousel container itself — arrows wrap, predictable
    root.addEventListener('keydown', function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-gallery-thumb')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(total - 1); }
    });

    // Keep state in sync when the user swipes/drag-scrolls the viewport
    var scrollTimer = null;
    viewport.addEventListener('scroll', function () {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        var mid = viewport.scrollLeft + viewport.clientWidth / 2;
        var nearest = 0;
        var best = Infinity;
        slides.forEach(function (s, i) {
          var center = s.offsetLeft - viewport.offsetLeft + s.offsetWidth / 2;
          var d = Math.abs(center - mid);
          if (d < best) { best = d; nearest = i; }
        });
        if (nearest !== index) { index = nearest; render(); }
      }, 120);
    }, { passive: true });

    // One extra boundary gesture past the end wraps to the other end.
    // Trackpad / wheel horizontal intent:
    viewport.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 8) return;
      tryBoundaryWrap(e.deltaX > 0 ? 1 : -1);
    }, { passive: true });

    // Touch drag past the edge:
    var touchStartX = 0;
    var touchStartScroll = 0;
    viewport.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches[0]) return;
      touchStartX = e.touches[0].clientX;
      touchStartScroll = viewport.scrollLeft;
    }, { passive: true });
    viewport.addEventListener('touchend', function (e) {
      var end = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : touchStartX;
      var dragged = end - touchStartX;                 // <0 => forward (toward next)
      var scrolled = Math.abs(viewport.scrollLeft - touchStartScroll);
      if (Math.abs(dragged) < 40 || scrolled > 6) return; // real scroll happened, not a boundary push
      tryBoundaryWrap(dragged < 0 ? 1 : -1);
    }, { passive: true });

    render();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    document.querySelectorAll('[data-aa-family-gallery]').forEach(initGallery);
  });
})();
